import { useFormikContext } from "formik";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import {
  ALL_TEMPLATE_PAGE_BUILDER_SECTIONS,
  TEMPLATE_ACTION,
  TEMPLATE_CAMPAIGN_BUILDER_SECTIONS
} from "./constants/template";
import { captureException } from "./error";
import Analytics, {
  // Page
  TEMPLATE_PAGE_BUILDER,
  EVENT_TEMPLATE_PAGE_BUILDER_BLOCK_PICKER,
  EVENT_TEMPLATE_PAGE_BUILDER_PRODUCT_PICKER,
  EVENT_TEMPLATE_PAGE_BUILDER_PRICE_PICKER,
  EVENT_TEMPLATE_PAGE_BUILDER_FEATURE_PICKER,
  // Campaign
  EVENT_TEMPLATE_CAMPAIGN_BUILDER_DETAILS,
  EVENT_TEMPLATE_CAMPAIGN_BUILDER_AUDIENCE,
  EVENT_TEMPLATE_CAMPAIGN_BUILDER_SEND,
  TEMPLATE_CAMPAIGN_BUILDER
} from "utils/analytics";
import { STATE_KEYS } from "./constants/state";
import { TEMPLATE_PAGE_TYPE } from "components/FormerEditor/common/constants/entity";

const PROGRESS_COPY = {
  COMPLETE: "Complete",
  NEXT: "Next",
  PREVIOUS: "Previous"
};

const EVENT_MAP = {
  [TEMPLATE_PAGE_BUILDER]: {
    [ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.BLOCKS]: EVENT_TEMPLATE_PAGE_BUILDER_BLOCK_PICKER,
    [ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.PRODUCTS]: EVENT_TEMPLATE_PAGE_BUILDER_PRODUCT_PICKER,
    [ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.PRICES]: EVENT_TEMPLATE_PAGE_BUILDER_PRICE_PICKER,
    [ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.FEATURES]: EVENT_TEMPLATE_PAGE_BUILDER_FEATURE_PICKER
  },
  [TEMPLATE_CAMPAIGN_BUILDER]: {
    [TEMPLATE_CAMPAIGN_BUILDER_SECTIONS.DETAILS]: EVENT_TEMPLATE_CAMPAIGN_BUILDER_DETAILS,
    [TEMPLATE_CAMPAIGN_BUILDER_SECTIONS.AUDIENCE]: EVENT_TEMPLATE_CAMPAIGN_BUILDER_AUDIENCE,
    [TEMPLATE_CAMPAIGN_BUILDER_SECTIONS.SEND]: EVENT_TEMPLATE_CAMPAIGN_BUILDER_SEND
  }
};

const TEMPLATE_PAGE_CAN_PROGRESS = {
  [TEMPLATE_PAGE_TYPE.PRICING]: ({
    activeSection,
    template,
    values,
    errors
  }) => {
    let canProgress = true;

    if (activeSection === ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.BLOCKS) {
      canProgress = values.entities.length > 0;
    } else if (activeSection === ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.PRODUCTS) {
      canProgress = values.products.length > 0;
    } else if (activeSection === ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.PRICES) {
      canProgress = isEmpty(errors);
    }
    return canProgress;
  },
  [TEMPLATE_PAGE_TYPE.STORE]: ({ activeSection, template, values, errors }) => {
    let canProgress = true;

    if (activeSection === ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.BLOCKS) {
      canProgress = values.entities.length > 0 && isEmpty(errors);
    } else if (activeSection === ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.PRODUCTS) {
      /**
       * Let the user continue even if they havent set any products
       * - This means users with fresh stripe accounts can create a page at a minimum
       */
      canProgress = true;
    }

    return canProgress;
  },
  [TEMPLATE_PAGE_TYPE.TIP]: ({ activeSection, template, values, errors }) => {
    let canProgress = true;

    if (activeSection === ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.BLOCKS) {
      canProgress = values.entities.length > 0 && isEmpty(errors);
    }

    return canProgress;
  }
};

export const CAN_PROGRESS_FN_MAP = {
  [TEMPLATE_PAGE_BUILDER]: ({
    activeSection,
    template,
    values,
    action,
    copyManifestUUID,
    errors
  }) => {
    if (action === TEMPLATE_ACTION.COPY) {
      return Boolean(copyManifestUUID);
    } else {
      const templateProgressFn = TEMPLATE_PAGE_CAN_PROGRESS[template];

      return templateProgressFn
        ? templateProgressFn({
            activeSection,
            template,
            values,
            errors,
            copyManifestUUID
          })
        : true;
    }
  },
  [TEMPLATE_CAMPAIGN_BUILDER]: ({ activeSection, values, errors }) => {
    let canProgress = true;

    if (activeSection === TEMPLATE_CAMPAIGN_BUILDER_SECTIONS.DETAILS) {
      canProgress = Boolean(values.title);
    } else if (activeSection === TEMPLATE_CAMPAIGN_BUILDER_SECTIONS.AUDIENCE) {
      canProgress = get(values, STATE_KEYS.CAMPAIGN.AUDIENCES, []).length > 0;
    } else if (activeSection === TEMPLATE_CAMPAIGN_BUILDER_SECTIONS.SEND) {
      canProgress = isEmpty(errors);
    }

    return canProgress;
  }
};

export const useTemplateContinue = ({ onNext, tracking } = {}) => {
  const {
    values,
    errors,
    setFieldValue,
    submitForm,
    isSubmitting
  } = useFormikContext();

  const {
    activeSection,
    template,
    action,
    copyManifestUUID,
    sections,
    tracking: initialTracking
  } = values.form;
  console.log("------- ~ template", template);
  console.log("------- ~ activeSection", activeSection);
  /**
   * Tracking is initialized in formik context via initialValues
   * - Optional additional tracking can be passed through
   */
  const mergedTracking = { ...initialTracking, ...tracking };
  const featureContext = mergedTracking.feature_context;
  const contextEventMap = EVENT_MAP[featureContext];
  const contextCanProgressFn = CAN_PROGRESS_FN_MAP[featureContext];

  const canProgress = contextCanProgressFn
    ? contextCanProgressFn({
        activeSection,
        action,
        copyManifestUUID,
        template,
        values,
        errors
      })
    : true;

  const sectionKeys = Object.keys(sections);
  const currentSectionIndex = sectionKeys.indexOf(activeSection);
  const prevDisabled = currentSectionIndex === 0 || isSubmitting;

  const prevCopy = PROGRESS_COPY.PREVIOUS;
  const prevSection = sectionKeys[currentSectionIndex - 1];
  const prevAction = () => {
    setFieldValue("form.activeSection", prevSection);
  };

  const isTheEnd = currentSectionIndex === sectionKeys.length - 1;
  const nextCopy = isTheEnd ? PROGRESS_COPY.COMPLETE : PROGRESS_COPY.NEXT;
  const nextSection = sectionKeys[currentSectionIndex + 1];
  const nextDisabled = !canProgress;

  const completingSection = sectionKeys[currentSectionIndex];
  const completingEvent = contextEventMap[completingSection];

  const trackSectionCompletion = () => {
    if (completingEvent) {
      Analytics.track(completingEvent, mergedTracking);
    } else {
      captureException(
        new Error(`No analytics event for ${completingSection}`)
      );
    }
  };
  let nextAction;
  if (onNext) {
    nextAction = () => {
      trackSectionCompletion();
      onNext();
    };
  } else {
    nextAction = isTheEnd
      ? () => {
          trackSectionCompletion();
          submitForm();
        }
      : () => {
          trackSectionCompletion();
          setFieldValue("form.activeSection", nextSection);
        };
  }

  return {
    isSubmitting,
    // Prev
    prevDisabled,
    prevCopy,
    prevAction,
    prevSection,
    // Next
    nextSection,
    nextDisabled,
    nextCopy,
    nextAction
  };
};
